import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useSort } from '@/lib/useSort';
import { SortableHeader } from '@/components/shared/SortableHeader';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Building2, Plus, Pencil, Archive, Users, ArrowRight, UserCog, ArrowLeft } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import DepartmentPositions from '@/components/admin/DepartmentPositions';
import { useDataLabel } from '@/lib/dataI18n';

const UNASSIGNED = '__unassigned__';

export default function AdminDepartments() {
  const { t } = useTranslation();
  const tData = useDataLabel();  // Session N-B — locale-aware dept name renderer
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedDeptId, setSelectedDeptId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', name_es: '', code: '' });

  useEffect(() => {
    (async () => {
      try {
        const cos = await adminApi.listCompanies();
        setCompanies(cos);
        const fromUrl = searchParams.get('company_id');
        if (fromUrl && cos.some((c) => c.id === fromUrl)) setCompanyId(fromUrl);
        else if (cos.length) setCompanyId(cos[0].id);
        const deptFromUrl = searchParams.get('dept');
        if (deptFromUrl) setSelectedDeptId(deptFromUrl);
      } catch { toast.error(t('toasts.failedToLoadCompanies')); }
    })();
  }, []);

  const reload = async (cid = companyId) => {
    if (!cid) return;
    setLoading(true);
    try {
      const [d, e] = await Promise.all([
        adminApi.listDepartments(cid, true),
        adminApi.listCompanyEmployees(cid),
      ]);
      setDepartments(d);
      setEmployees(e);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [companyId]);

  const create = async () => {
    if (!form.name?.trim()) return toast.error(t('toasts.nameRequired'));
    try {
      await adminApi.createDepartment({
        company_id: companyId,
        name: form.name.trim(),
        name_es: form.name_es?.trim() || null,  // Session N-B
        code: form.code?.trim() || null,
      });
      toast.success(t('common.created', { defaultValue: 'Created' }));
      setCreateOpen(false); setForm({ name: '', name_es: '', code: '' });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (d) => {
    setEditing(d);
    setForm({ name: d.name, name_es: d.name_es || '', code: d.code || '' });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    try {
      await adminApi.updateDepartment(editing.id, {
        name: form.name.trim(),
        name_es: form.name_es?.trim() || null,  // Session N-B
        code: form.code?.trim() || null,
      });
      toast.success(t('common.saved', { defaultValue: 'Saved' }));
      setEditOpen(false); setEditing(null);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const archive = async (d) => {
    try {
      await adminApi.updateDepartment(d.id, { archived: !d.archived });
      toast.success(d.archived
        ? t('common.unarchived', { defaultValue: 'Restored' })
        : t('common.archived', { defaultValue: 'Archived' }));
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const reassign = async (employeeId, newDeptId) => {
    try {
      await adminApi.saveCompensation(employeeId, {
        department_id: newDeptId === UNASSIGNED ? null : newDeptId,
      });
      toast.success(t('common.saved', { defaultValue: 'Saved' }));
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  // Phase 3 — Department Responsible (organizational only, no RBAC impact).
  const saveResponsible = async (deptId, v) => {
    try {
      await adminApi.updateDepartment(deptId, { responsible_id: v === '__none__' ? null : v });
      toast.success(t('common.saved', { defaultValue: 'Saved' }));
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const visibleDepts = useMemo(() => departments.filter(d => !d.archived), [departments]);
  const archivedDepts = useMemo(() => departments.filter(d => d.archived), [departments]);

  const employeesByDept = useMemo(() => {
    const map = { [UNASSIGNED]: [] };
    departments.forEach(d => { map[d.id] = []; });
    employees.forEach(e => {
      const k = e.department_id || UNASSIGNED;
      (map[k] = map[k] || []).push(e);
    });
    return map;
  }, [departments, employees]);

  const selectedDept = selectedDeptId === UNASSIGNED
    ? { id: UNASSIGNED, name: t('intelligence.unassigned', { defaultValue: 'Unassigned' }), code: '—' }
    : departments.find(d => d.id === selectedDeptId);
  const selectedEmployeesRaw = selectedDeptId
    ? (employeesByDept[selectedDeptId] || [])
    : [];
  const sort = useSort();
  const selectedEmployees = useMemo(() => sort.apply(selectedEmployeesRaw, {
    name:     (e) => (e.name || '').toLowerCase(),
    position: (e) => (e.position || '').toLowerCase(),
  }), [selectedEmployeesRaw, sort]);

  return (
    <>
      <Topbar
        title={t('departments.title', { defaultValue: 'Departments' })}
        subtitle={t('departments.subtitle', { defaultValue: 'Organise employees into departments to unlock per-team intelligence' })}
        leading={companyId ? (
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-muted-foreground hover:text-foreground"
            onClick={() => navigate(`/admin/clients/${companyId}`)}
            data-testid="dept-back-to-client"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('phase3.nav.backToClient', { defaultValue: 'Client profile' })}
          </Button>
        ) : undefined}
        actions={
          <div className="flex items-center gap-2">
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-56" data-testid="dept-company-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="dept-new-btn"><Plus className="h-4 w-4 mr-1.5" />{t('departments.new', { defaultValue: 'New department' })}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('departments.newTitle', { defaultValue: 'New department' })}</DialogTitle>
                  <DialogDescription>{t('departments.newDesc', { defaultValue: 'Add a department to this company.' })}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div><Label>{t('departments.name', { defaultValue: 'Name (English)' })}</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="dept-name-input" /></div>
                  <div>
                    <Label>{t('departments.nameEs', { defaultValue: 'Name (Spanish, optional)' })}</Label>
                    <Input
                      value={form.name_es}
                      onChange={(e)=>setForm({...form, name_es:e.target.value})}
                      placeholder={t('departments.nameEsPh', { defaultValue: 'Leave blank to fall back to English' })}
                      data-testid="dept-name-es-input"
                    />
                  </div>
                  <div><Label>{t('departments.code', { defaultValue: 'Code (optional)' })}</Label><Input value={form.code} onChange={(e)=>setForm({...form, code:e.target.value})} maxLength={20} placeholder={t('pages.adminDepartments.codePh')} data-testid="dept-code-input" /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={()=>setCreateOpen(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
                  <Button onClick={create} data-testid="dept-create-btn">{t('common.create', { defaultValue: 'Create' })}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="p-6 lg:p-8 grid lg:grid-cols-3 gap-6">
        {/* Department list */}
        <Card className="p-5 lg:col-span-1 shadow-card">
          <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> {t('departments.list', { defaultValue: 'Departments' })}</h3>
          <div className="space-y-1.5">
            {visibleDepts.map(d => (
              <button key={d.id} onClick={()=>setSelectedDeptId(d.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg border transition ${selectedDeptId===d.id ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-muted/40'}`}
                data-testid={`dept-item-${d.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{tData(d)}</div>
                    {d.code && <div className="text-xs text-muted-foreground">{d.code}</div>}
                  </div>
                  <Badge variant="secondary" className="text-xs tabular-nums shrink-0">
                    {(employeesByDept[d.id]||[]).length} <Users className="h-3 w-3 ml-1" />
                  </Badge>
                </div>
                {(() => {
                  const un = (employeesByDept[d.id] || []).filter((e) => !e.position_id).length;
                  return un > 0 ? (
                    <p className="text-[11px] text-amber-700/90 mt-1" data-testid={`dept-unlinked-${d.id}`}>
                      {t('phase3.reconcile.deptHint', { count: un, defaultValue: '{{count}} without a linked formal position' })}
                    </p>
                  ) : null;
                })()}
              </button>
            ))}
            <button onClick={()=>setSelectedDeptId(UNASSIGNED)}
              className={`w-full text-left px-3 py-2.5 rounded-lg border-dashed border transition ${selectedDeptId===UNASSIGNED ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}
              data-testid="dept-unassigned"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm italic text-muted-foreground">{t('intelligence.unassigned', { defaultValue: 'Unassigned' })}</span>
                <Badge variant="outline" className="text-xs tabular-nums">
                  {(employeesByDept[UNASSIGNED]||[]).length}
                </Badge>
              </div>
            </button>
            {!visibleDepts.length && !loading && (
              <p className="text-xs text-muted-foreground italic px-3 py-3">{t('departments.empty', { defaultValue: 'No departments yet — create your first one.' })}</p>
            )}
          </div>
          {archivedDepts.length > 0 && (
            <details className="mt-4 text-xs">
              <summary className="cursor-pointer text-muted-foreground">{t('departments.archived', { defaultValue: 'Archived' })} ({archivedDepts.length})</summary>
              <div className="space-y-1 mt-2">
                {archivedDepts.map(d => (
                  <div key={d.id} className="flex items-center justify-between px-2 py-1.5 rounded-md bg-muted/30">
                    <span className="text-muted-foreground">{tData(d)}</span>
                    <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={()=>archive(d)}>{t('common.restore', { defaultValue: 'Restore' })}</Button>
                  </div>
                ))}
              </div>
            </details>
          )}
        </Card>

        {/* Detail panel */}
        <Card className="p-6 lg:col-span-2 shadow-card">
          {!selectedDept ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              {t('departments.selectPrompt', { defaultValue: 'Pick a department on the left to manage its members.' })}
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-display font-bold text-xl tracking-tight">{tData(selectedDept)}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{selectedDept.code || '—'} · {t('intelligence.employees', { count: selectedEmployees.length, defaultValue: '{{count}} employees' })}</p>
                </div>
                {selectedDeptId !== UNASSIGNED && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={()=>startEdit(selectedDept)} data-testid="dept-edit-btn"><Pencil className="h-3.5 w-3.5 mr-1.5" />{t('common.edit', { defaultValue: 'Edit' })}</Button>
                    <Button size="sm" variant="outline" onClick={()=>archive(selectedDept)} data-testid="dept-archive-btn"><Archive className="h-3.5 w-3.5 mr-1.5" />{selectedDept.archived ? t('common.restore', { defaultValue: 'Restore' }) : t('common.archive', { defaultValue: 'Archive' })}</Button>
                  </div>
                )}
              </div>

              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                    <th className="py-2 pr-3"><SortableHeader sort={sort} k="name">{t('intelligence.employee', { defaultValue: 'Employee' })}</SortableHeader></th>
                    <th className="py-2 px-3"><SortableHeader sort={sort} k="position">{t('intelligence.position', { defaultValue: 'Position' })}</SortableHeader></th>
                    <th className="py-2 pl-3">{t('departments.assignTo', { defaultValue: 'Move to' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedEmployees.map(e => (
                    <tr key={e.id} className="border-b last:border-0">
                      <td className="py-3 pr-3">
                        <div className="font-medium">{e.name}</div>
                        <div className="text-xs text-muted-foreground">{e.email}</div>
                      </td>
                      <td className="py-3 px-3 text-muted-foreground">{e.position || '—'}</td>
                      <td className="py-3 pl-3">
                        <Select value={e.department_id || UNASSIGNED} onValueChange={(v)=>reassign(e.id, v)}>
                          <SelectTrigger className="h-8 text-xs w-48" data-testid={`dept-reassign-${e.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value={UNASSIGNED}>{t('intelligence.unassigned', { defaultValue: 'Unassigned' })}</SelectItem>
                            {visibleDepts.map(d => <SelectItem key={d.id} value={d.id}>{tData(d)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                  {!selectedEmployees.length && (
                    <tr><td colSpan={3} className="py-6 text-center text-sm text-muted-foreground italic">
                      {t('departments.empty_dept', { defaultValue: 'No employees in this department yet.' })}
                    </td></tr>
                  )}
                </tbody>
              </table>

              {/* Phase 3 — Department Responsible + Positions */}
              {selectedDeptId !== UNASSIGNED && (
                <>
                  <div className="mt-6 pt-5 border-t border-border" data-testid="dept-responsible-block">
                    <h4 className="font-display font-bold text-base flex items-center gap-2">
                      <UserCog className="h-4 w-4 text-primary" />
                      {t('phase3.dept.responsible', { defaultValue: 'Department responsible' })}
                    </h4>
                    <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                      {t('phase3.dept.responsibleHint', { defaultValue: 'Organizational relationship only — assigning a responsible person does NOT grant any permission or access.' })}
                    </p>
                    <Select
                      value={selectedDept.responsible_id || '__none__'}
                      onValueChange={(v) => saveResponsible(selectedDept.id, v)}
                    >
                      <SelectTrigger className="w-full max-w-sm" data-testid="dept-responsible-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t('phase3.dept.noResponsible', { defaultValue: 'No responsible assigned' })}</SelectItem>
                        {(employeesByDept[selectedDept.id] || []).length > 0 && (
                          <SelectGroup>
                            <SelectLabel>{t('phase3.dept.deptMembers', { defaultValue: 'Department members' })}</SelectLabel>
                            {(employeesByDept[selectedDept.id] || []).map((e) => (
                              <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                            ))}
                          </SelectGroup>
                        )}
                        <SelectGroup>
                          <SelectLabel>{t('phase3.dept.otherEmployees', { defaultValue: 'Other company employees' })}</SelectLabel>
                          {employees.filter((e) => e.department_id !== selectedDept.id).map((e) => (
                            <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  <DepartmentPositions
                    companyId={companyId}
                    department={selectedDept}
                    employees={employees}
                    onChanged={reload}
                  />
                </>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('departments.editTitle', { defaultValue: 'Edit department' })}</DialogTitle><DialogDescription>{selectedDept?.name}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t('departments.name', { defaultValue: 'Name (English)' })}</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} /></div>
            <div>
              <Label>{t('departments.nameEs', { defaultValue: 'Name (Spanish, optional)' })}</Label>
              <Input
                value={form.name_es}
                onChange={(e)=>setForm({...form, name_es:e.target.value})}
                placeholder={t('departments.nameEsPh', { defaultValue: 'Leave blank to fall back to English' })}
                data-testid="dept-name-es-edit-input"
              />
            </div>
            <div><Label>{t('departments.code', { defaultValue: 'Code' })}</Label><Input value={form.code} onChange={(e)=>setForm({...form, code:e.target.value})} maxLength={20} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditOpen(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button onClick={saveEdit} data-testid="dept-save-btn">{t('common.save', { defaultValue: 'Save' })}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
